import { useState } from 'react';

import { api } from '../api/client';
import { Badge, Button, Card, Field, Form, Loading, Notice, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';
import { article } from '../vocabulary';

/**
 * Tags — who may visit where.
 *
 * A tag is a label the client invents: "north region", "refrigerated", "night shift". A
 * place carries tags, a person carries tags, and a person is only sent to places they
 * share a tag with.
 *
 * The rule worth stating on screen, because it is the one that surprises people: **a place
 * with no tags is open to everyone.** Restriction is opt-in, so adding tags to one corner
 * of an estate does not silently strand the rest of it.
 */

export interface TagRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  colour: string | null;
  siteCount: number;
  /** Null where the caller only sees their own places — an estate-wide count is not theirs. */
  userCount: number | null;
}

export function Tags(): JSX.Element {
  const { can, config } = useSession();
  const tags = useResource(() => api.get<TagRow[]>('/tags'), []);
  const { run, error, busy } = useAction();
  const [name, setName] = useState('');

  /** Which tag is being renamed, and to what. */
  const [editing, setEditing] = useState<{ id: string; name: string; description: string } | null>(
    null,
  );

  const create = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post('/tags', { name });
    });
    if (ok) {
      setName('');
      tags.reload();
    }
  };

  const save = async (): Promise<void> => {
    if (editing === null) return;

    const ok = await run(async () => {
      await api.patch(`/tags/${editing.id}`, {
        name: editing.name,
        description: editing.description === '' ? null : editing.description,
      });
    });

    if (ok) {
      setEditing(null);
      tags.reload();
    }
  };

  const remove = async (id: string): Promise<void> => {
    const ok = await run(async () => {
      await api.delete(`/tags/${id}`);
    });
    if (ok) tags.reload();
  };

  return (
    <>
      <Card title="Tags">
        <Notice kind="info">
          {article(config.siteLabel) === 'an' ? 'An' : 'A'} {config.siteLabel.toLowerCase()} with
          no tags can be visited by anyone. Once it
          has tags, only people sharing one of them are scheduled to it.
        </Notice>

        {error !== null && <Notice kind="error">{error}</Notice>}

        {tags.loading ? (
          <Loading />
        ) : tags.error !== null ? (
          <Notice kind="error">{tags.error}</Notice>
        ) : (
          <Table
            rows={tags.data ?? []}
            keyOf={(row) => row.id}
            empty="No tags yet. Everywhere is open to everyone."
            columns={[
              { header: 'Tag', render: (row) => <Badge tone="low">{row.name}</Badge> },
              { header: 'Description', render: (row) => row.description ?? '—' },
              { header: `${config.siteLabel}s`, numeric: true, render: (row) => row.siteCount },
              {
                // Null for a caller scoped to their own places: a count over the whole
                // estate is not a fact about their store, and a wrong number is worse than
                // an absent one.
                header: `${config.fieldUserLabel}s`,
                numeric: true,
                render: (row) => row.userCount ?? '—',
              },
              // Offered only to somebody who can use them. A store manager may read tags
              // — they explain why a form is asked at their store — but every one of these
              // buttons answers with a refusal from the server.
              ...(can('site.edit') ? [{
                header: '',
                render: (row: TagRow) => (
                  <div className="row">
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        setEditing({
                          id: row.id,
                          name: row.name,
                          description: row.description ?? '',
                        })
                      }
                    >
                      Edit
                    </Button>
                    <Button variant="danger" onClick={() => void remove(row.id)} disabled={busy}>
                      Delete
                    </Button>
                  </div>
                ),
              }] : []),
            ]}
          />
        )}
      </Card>

      {editing !== null && (
        <Card title={`Edit tag`}>
          <Form onSubmit={() => void save()}>
            <div className="form-row">
              <Field
                label="Tag name"
                value={editing.name}
                onChange={(value) => setEditing({ ...editing, name: value })}
                required
              />
              <Field
                label="Description"
                value={editing.description}
                onChange={(value) => setEditing({ ...editing, description: value })}
                hint="Optional. Shown here only."
              />
            </div>

            {/* Said plainly: the rename is cosmetic and changes nothing about who goes where. */}
            <p className="muted">
              Renaming a tag keeps every {config.siteLabel.toLowerCase()} and{' '}
              {config.fieldUserLabel.toLowerCase()} already carrying it, and leaves its
              key unchanged so imports and exports keep matching.
            </p>

            <div className="form-actions">
              <Button type="submit" disabled={busy || editing.name === ''}>
                {busy ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {can('site.edit') && (
      <Card title="Add a tag">
        <Form onSubmit={() => void create()}>
          <div className="form-row">
            <Field
              label="Tag name"
              value={name}
              onChange={setName}
              required
              hint="e.g. North region, Refrigerated, High security"
            />
          </div>
          <div className="form-actions">
            <Button type="submit" disabled={busy || name === ''}>
              {busy ? 'Adding…' : 'Add tag'}
            </Button>
          </div>
        </Form>
      </Card>
      )}
    </>
  );
}

/**
 * Assign tags to a place or a person.
 *
 * Shared by both screens because it is the same decision from two directions, and two
 * copies would drift.
 */
export function TagPicker({
  ownerId,
  kind,
  onSaved,
}: {
  ownerId: string;
  kind: 'site' | 'user';
  onSaved?: () => void;
}): JSX.Element {
  const path = kind === 'site' ? `/sites/${ownerId}/tags` : `/field-users/${ownerId}/tags`;

  const all = useResource(() => api.get<TagRow[]>('/tags'), []);
  const mine = useResource(() => api.get<TagRow[]>(path), [ownerId]);
  const { run, error, busy } = useAction();
  const [selected, setSelected] = useState<Set<string> | null>(null);

  // Seeded from the server the first time, then owned by the component so a click is
  // instant rather than waiting for a round trip.
  const current = selected ?? new Set((mine.data ?? []).map((tag) => tag.id));

  const toggle = (id: string): void => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const save = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.put(path, { tagIds: [...current] });
    });
    if (ok) {
      mine.reload();
      onSaved?.();
    }
  };

  if (all.loading || mine.loading) return <Loading />;
  if ((all.data ?? []).length === 0) {
    return <p className="muted">No tags exist yet, so there is nothing to restrict.</p>;
  }

  return (
    <div className="stack">
      {error !== null && <Notice kind="error">{error}</Notice>}

      <div className="permission-grid">
        {(all.data ?? []).map((tag) => (
          <label key={tag.id}>
            <input type="checkbox" checked={current.has(tag.id)} onChange={() => toggle(tag.id)} />
            <span>{tag.name}</span>
          </label>
        ))}
      </div>

      <div className="form-actions">
        <Button onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : `Save ${current.size} tag(s)`}
        </Button>
      </div>

      {current.size === 0 && kind === 'site' && (
        <p className="muted">With no tags, anyone can be scheduled here.</p>
      )}
      {current.size === 0 && kind === 'user' && (
        <p className="muted">With no tags, this person can be scheduled to untagged places only.</p>
      )}
    </div>
  );
}
